import {
  Box,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { LockOutlined, SearchOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useCallback, useState } from "react"

import { InventoryAdjustmentLotStatusIcon } from "./InventoryAdjustmentLotStatusIcon"

import SyncWarningAlert from "@/components/common/Alerts/SyncWarningAlert"
import type { InventoryPalletGroup, InventoryPalletLot, ProductPackagingType } from "@/graphql/codegen/graphql"
import { InventoryPalletGroupsSortField, SortingOrder } from "@/graphql/codegen/graphql"
import { calculateRemainingShelfLife } from "@/screens/Tablet/utils/utils"
import { getProductPackagingTypeLabel } from "@/utils/product"

const sortLabelSx = {
  "&.Mui-active": { color: "inherit" },
  "& .MuiTableSortLabel-icon": { color: "inherit !important" },
} as const

function getPalletTooltipTitle(isSyncing: boolean, isPalletDisabled: boolean): string {
  if (isSyncing) return "Inventory is being synchronized with AS400"
  if (isPalletDisabled) return "All lots on this pallet are locked by pending adjustments"
  return ""
}

function getLotTooltipTitle(isSyncing: boolean, isLotLocked: boolean): string {
  if (isSyncing) return "Inventory is being synchronized with AS400"
  if (isLotLocked) return "Lot is locked by a pending adjustment"
  return ""
}

interface InventoryAdjustmentPalletsSelectionSectionProps {
  palletGroups: InventoryPalletGroup[]
  isFetching: boolean
  selectedLotIds: Set<string>
  sortBy: InventoryPalletGroupsSortField
  sortDirection: SortingOrder
  isSyncing: boolean
  uomName: string
  onSelectionChange: (lotIds: Set<string>) => void
  onSortChange: (field: InventoryPalletGroupsSortField, direction: SortingOrder) => void
  onSearchChange: (search: string) => void
}

export const InventoryAdjustmentPalletsSelectionSection: FC<InventoryAdjustmentPalletsSelectionSectionProps> = ({
  palletGroups,
  isFetching,
  selectedLotIds,
  sortBy,
  sortDirection,
  isSyncing,
  uomName,
  onSelectionChange,
  onSortChange,
  onSearchChange,
}) => {
  const [search, setSearch] = useState("")
  const sortDirectionLabel = sortDirection === SortingOrder.Asc ? "asc" : "desc"

  const handleSortClick = useCallback(
    (field: InventoryPalletGroupsSortField) => {
      let newSortDirection = SortingOrder.Asc

      if (field === sortBy) {
        newSortDirection = sortDirection === SortingOrder.Asc ? SortingOrder.Desc : SortingOrder.Asc
      }

      onSortChange(field, newSortDirection)
    },
    [sortBy, sortDirection, onSortChange]
  )

  const getPalletCheckboxProps = useCallback(
    (pallet: InventoryPalletGroup) => {
      const selectable = pallet.lots.filter((lot) => !lot.isLocked)

      if (!selectable.length) {
        return { checked: false, indeterminate: false, disabled: true }
      }

      const selectedCount = selectable.filter((lot) => selectedLotIds.has(lot.rawInventoryId)).length

      return {
        checked: selectedCount === selectable.length,
        indeterminate: selectedCount > 0 && selectedCount < selectable.length,
        disabled: false,
      }
    },
    [selectedLotIds]
  )

  const handlePalletCheckboxChange = useCallback(
    (pallet: InventoryPalletGroup) => {
      const selectable = pallet.lots.filter((lot) => !lot.isLocked)
      const allSelected = selectable.every((lot) => selectedLotIds.has(lot.rawInventoryId))
      const next = new Set(selectedLotIds)
      selectable.forEach((lot) => (allSelected ? next.delete(lot.rawInventoryId) : next.add(lot.rawInventoryId)))
      onSelectionChange(next)
    },
    [selectedLotIds, onSelectionChange]
  )

  const handleLotCheckboxChange = useCallback(
    (lot: InventoryPalletLot) => {
      const next = new Set(selectedLotIds)

      if (next.has(lot.rawInventoryId)) {
        next.delete(lot.rawInventoryId)
      } else {
        next.add(lot.rawInventoryId)
      }

      onSelectionChange(next)
    },
    [selectedLotIds, onSelectionChange]
  )

  return (
    <Box className='rounded-lg border border-gray-300 p-4'>
      <Box className='mb-4 flex items-center justify-between'>
        <Typography variant='subtitle1' className='font-bold'>
          1. Select Pallets and Lots
        </Typography>
        <TextField
          size='small'
          placeholder='Search by Reference #, Lot or Batch...'
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            onSearchChange(e.target.value)
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position='end'>
                  <SearchOutlined fontSize='small' />
                </InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: 360 }}
          data-testid='inventory-adjustment.quantity-step.section1-search'
        />
      </Box>
      {isSyncing && <SyncWarningAlert />}
      <Box className='relative'>
        {isFetching && (
          <Box className='absolute inset-0 z-10 flex items-center justify-center bg-white/60'>
            <CircularProgress size={24} />
          </Box>
        )}
        <TableContainer sx={{ maxHeight: 360, overflow: "auto" }}>
          <Table size='small' stickyHeader>
            <TableHead className='bg-gray-100'>
              <TableRow>
                {/* Pallet checkbox column */}
                <TableCell sx={{ width: 48 }} />
                <TableCell sx={{ minWidth: 100, whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    sx={sortLabelSx}
                    active={sortBy === InventoryPalletGroupsSortField.ReferenceNbr}
                    direction={sortBy === InventoryPalletGroupsSortField.ReferenceNbr ? sortDirectionLabel : "asc"}
                    onClick={() => handleSortClick(InventoryPalletGroupsSortField.ReferenceNbr)}
                  >
                    Reference #
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 90, whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    sx={sortLabelSx}
                    active={sortBy === InventoryPalletGroupsSortField.Location}
                    direction={sortBy === InventoryPalletGroupsSortField.Location ? sortDirectionLabel : "asc"}
                    onClick={() => handleSortClick(InventoryPalletGroupsSortField.Location)}
                  >
                    Location
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 80, whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    sx={sortLabelSx}
                    active={sortBy === InventoryPalletGroupsSortField.TotalQoh}
                    direction={sortBy === InventoryPalletGroupsSortField.TotalQoh ? sortDirectionLabel : "asc"}
                    onClick={() => handleSortClick(InventoryPalletGroupsSortField.TotalQoh)}
                  >
                    Total QoH
                  </TableSortLabel>
                </TableCell>
                {/* Lot checkbox column */}
                <TableCell sx={{ width: 48 }} />
                <TableCell sx={{ minWidth: 80, whiteSpace: "nowrap" }}>Lot #</TableCell>
                <TableCell sx={{ minWidth: 80, whiteSpace: "nowrap" }}>Batch #</TableCell>
                <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    sx={sortLabelSx}
                    active={sortBy === InventoryPalletGroupsSortField.ExpirationDate}
                    direction={sortBy === InventoryPalletGroupsSortField.ExpirationDate ? sortDirectionLabel : "asc"}
                    onClick={() => handleSortClick(InventoryPalletGroupsSortField.ExpirationDate)}
                  >
                    Remaining Shelf Life
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 90, whiteSpace: "nowrap" }}>Packaging</TableCell>
                <TableCell sx={{ minWidth: 60, whiteSpace: "nowrap" }}>Units</TableCell>
                <TableCell sx={{ minWidth: 70, whiteSpace: "nowrap" }}>Pkg UoM</TableCell>
                <TableCell sx={{ minWidth: 70, whiteSpace: "nowrap" }}>Lot UoM</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {!palletGroups.length && (
                <TableRow>
                  <TableCell colSpan={12} align='center' className='py-8 text-gray-500'>
                    No pallets found
                  </TableCell>
                </TableRow>
              )}

              {palletGroups.map((pallet) => {
                const palletCheckboxProps = getPalletCheckboxProps(pallet)
                const { disabled: isPalletDisabled } = palletCheckboxProps
                const isAnyLotLocked = pallet.lots.some((lot) => lot.isLocked)
                const palletTooltipTitle = getPalletTooltipTitle(!!isSyncing, isPalletDisabled)

                return pallet.lots.map((lot, lotIndex) => {
                  const isFirstLot = lotIndex === 0
                  const remainingShelfLife =
                    lot.expirationDate && lot.manufacturingDate
                      ? calculateRemainingShelfLife(lot.manufacturingDate, lot.expirationDate)
                      : null
                  const lotTooltipTitle = getLotTooltipTitle(!!isSyncing, lot.isLocked)

                  return (
                    <TableRow
                      key={lot.rawInventoryId}
                      data-testid={`inventory-adjustment.select-pallets.lot-row-${lot.rawInventoryId}`}
                    >
                      {/* Pallet checkbox — only on first lot */}
                      {isFirstLot ? (
                        <Tooltip title={palletTooltipTitle} disableHoverListener={!palletTooltipTitle}>
                          <TableCell>
                            <Checkbox
                              size='small'
                              {...palletCheckboxProps}
                              disabled={isSyncing || isPalletDisabled}
                              onChange={() => handlePalletCheckboxChange(pallet)}
                              data-testid={`inventory-adjustment.select-pallets.pallet-checkbox-${pallet.referenceNbr}`}
                            />
                          </TableCell>
                        </Tooltip>
                      ) : (
                        <TableCell />
                      )}
                      <TableCell>
                        {isFirstLot && (
                          <Box className='flex items-center gap-x-1'>
                            {pallet.referenceNbr}
                            {isAnyLotLocked && (
                              <Tooltip title='Some lots are locked'>
                                <LockOutlined className='text-gray-600' />
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{isFirstLot && pallet.location}</TableCell>
                      <TableCell>{isFirstLot && `${pallet.totalQoh} ${uomName}`}</TableCell>
                      <Tooltip title={lotTooltipTitle} disableHoverListener={!lotTooltipTitle}>
                        <TableCell>
                          <Checkbox
                            size='small'
                            checked={selectedLotIds.has(lot.rawInventoryId)}
                            disabled={isSyncing || lot.isLocked}
                            onChange={() => handleLotCheckboxChange(lot)}
                            data-testid={`inventory-adjustment.select-pallets.lot-checkbox-${lot.rawInventoryId}`}
                          />
                        </TableCell>
                      </Tooltip>
                      <TableCell>
                        <Box className='flex items-center gap-x-1'>
                          <InventoryAdjustmentLotStatusIcon qaStatus={lot.qaStatus} />
                          {lot.lotNbr ?? ""}
                        </Box>
                      </TableCell>
                      <TableCell>{lot.batchNbr ?? ""}</TableCell>
                      <TableCell>{remainingShelfLife === null ? "" : `${remainingShelfLife} %`}</TableCell>
                      <TableCell>{getProductPackagingTypeLabel(lot.packageDesc as ProductPackagingType)}</TableCell>
                      <TableCell>{lot.totalPackages}</TableCell>
                      <TableCell>
                        {lot.packageSize} {uomName}
                      </TableCell>
                      <TableCell>
                        {lot.totalUnits} {uomName}
                      </TableCell>
                    </TableRow>
                  )
                })
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  )
}
